import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Circle, Sparkles, TrendingUp, Target, Rocket, Loader2, Compass, AlertTriangle, MessageSquare, Wand2, FileText, ShieldCheck, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { OOHPatentBadge, OOHBriefDialog } from '@/components/OOH';
import MilestoneToolDialog from '@/components/MilestoneToolDialog';
import EvidenceViewDialog from '@/components/EvidenceViewDialog';
import DDPacketPanel from '@/components/DDPacketPanel';

/* -------------------------------------------------------------------------- */
/*  Scaling Lean Roadmap — per-startup guidance + milestone tracker           */
/*  Route: /dashboard/startups/:startupId/roadmap                             */
/* -------------------------------------------------------------------------- */

const STAGE_ICONS = {
  problem_solution_fit: Target,
  product_market_fit:   TrendingUp,
  scale:                Rocket,
};

function ProgressRing({ percent, size = 64, color = '#8B3FC5' }) {
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  const dash = (percent / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="currentColor" strokeOpacity="0.1" strokeWidth="4"/>
      <circle
        cx={size/2} cy={size/2} r={r} fill="none"
        stroke={color} strokeWidth="4"
        strokeDasharray={`${dash} ${c - dash}`}
        strokeDashoffset={c / 4}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dasharray 0.4s ease' }}
      />
      <text x={size/2} y={size/2 + 4} textAnchor="middle" fontSize="13" fontWeight="700" fill={color}>
        {Math.round(percent)}%
      </text>
    </svg>
  );
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect. */
export default function StartupRoadmap() {
  const { startupId } = useParams();
  const [framework, setFramework] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [guidance, setGuidance] = useState(null);
  const [gLoading, setGLoading] = useState(false);
  // OOH brief dialog + per-milestone AI toolkit dialog + evidence viewer
  const [oohOpen, setOohOpen] = useState(false);
  const [toolMilestone, setToolMilestone] = useState(null);
  const [evidenceMilestone, setEvidenceMilestone] = useState(null);
  const [toolCatalog, setToolCatalog] = useState(null);

  const load = async () => {
    try {
      const [fw, rm, gd, tc] = await Promise.all([
        api.get('/scaling-lean/framework').then(({ data }) => data),
        api.get(`/scaling-lean/roadmap/${startupId}`).then(({ data }) => data),
        api.get(`/scaling-lean/roadmap/${startupId}/guidance`).then(({ data }) => data).catch(() => null),
        api.get('/scaling-lean/milestone-tools/catalog').then(({ data }) => data?.tools || {}).catch(() => ({})),
      ]);
      setFramework(fw);
      setRoadmap(rm);
      setGuidance(gd);
      setToolCatalog(tc);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load roadmap.');
    } finally { setLoading(false); }
  };

  const generateGuidance = async () => {
    setGLoading(true);
    try {
      const { data } = await api.post(`/scaling-lean/roadmap/${startupId}/guidance`);
      setGuidance(data);
      toast.success('Yusuf has weighed in. Read his guidance below.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate guidance.');
    } finally { setGLoading(false); }
  };
  useEffect(() => { load(); }, [startupId]);

  const completed = useMemo(() => new Set(roadmap?.completed || []), [roadmap]);

  // Split sections into sequential stages vs orthogonal pillars
  const { stagesArr, pillarsArr } = useMemo(() => {
    if (!framework) return { stagesArr: [], pillarsArr: [] };
    const s = [];
    const p = [];
    for (const [id, section] of Object.entries(framework)) {
      if ((section.kind || 'stage') === 'pillar') p.push([id, section]);
      else s.push([id, section]);
    }
    return { stagesArr: s, pillarsArr: p };
  }, [framework]);

  const toggle = async (milestoneId, willComplete) => {
    setSavingId(milestoneId);
    try {
      const { data } = await api.post(`/scaling-lean/roadmap/${startupId}/toggle`, {
        milestone_id: milestoneId,
        completed: willComplete,
      });
      setRoadmap(data);
      if (willComplete) toast.success('Milestone cleared. OOH score recomputed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally { setSavingId(null); }
  };

  if (loading) {
    return <div className="ve-section inline-flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading roadmap…</div>;
  }
  if (!framework || !roadmap) return null;

  const stages = Object.entries(framework);

  /**
   * MilestoneCard — non-button wrapper so we can host BOTH the toggle
   * (click anywhere on the row → tick) AND a separate "AI Tool" button.
   * Nested buttons are invalid HTML; this is the canonical refactor.
   *
   * If the milestone has saved AI evidence, an "AI artefact" pill is
   * shown alongside an Eye button that opens the read-only viewer.
   * Hovering the pill exposes a short preview of the first artefact
   * field so the founder can scan the wall of milestones without
   * opening each one.
   */
  const MilestoneCard = ({ m }) => {
    const isDone = completed.has(m.id);
    const isSaving = savingId === m.id;
    const ts = roadmap.timestamps?.[m.id];
    const evidence = roadmap.evidence?.[m.id];
    const hasEvidence = !!evidence;

    // First-field preview (≤140 chars, plain string) — used as the hover tooltip
    let preview = null;
    if (evidence?.draft) {
      const firstKey = Object.keys(evidence.draft)[0];
      const firstVal = firstKey ? evidence.draft[firstKey] : null;
      const raw = Array.isArray(firstVal) ? firstVal.join(' · ') : String(firstVal ?? '');
      preview = raw.length > 140 ? raw.slice(0, 137) + '…' : raw;
    }
    return (
      <div
        data-testid={`milestone-${m.id}`}
        className={`ve-card p-4 lg:p-5 transition group hover:border-[#8B3FC5]/50 ${isDone ? 'bg-[#FFF8E5]/40 dark:bg-[#3a2f0a]/30 border-[#8B3FC5]/30' : ''}`}
      >
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => toggle(m.id, !isDone)}
            disabled={isSaving}
            aria-label={isDone ? `Mark ${m.label} as incomplete` : `Mark ${m.label} as complete`}
            data-testid={`milestone-toggle-${m.id}`}
            className={`mt-0.5 h-6 w-6 rounded-full border-2 shrink-0 flex items-center justify-center transition ${isDone ? 'bg-[#8B3FC5] border-[#8B3FC5]' : 'border-slate-300 dark:border-white/20 group-hover:border-[#8B3FC5]'}`}
          >
            {isSaving
              ? <Loader2 size={11} className="animate-spin text-slate-500" />
              : isDone
                ? <Check size={13} className="text-white" strokeWidth={3} />
                : <Circle size={11} className="text-transparent" />}
          </button>
          <button
            type="button"
            onClick={() => toggle(m.id, !isDone)}
            disabled={isSaving}
            className="flex-1 min-w-0 text-left cursor-pointer"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h4 className={`text-sm font-bold ${isDone ? 'line-through text-slate-500 dark:text-white/55' : ''}`}>{m.label}</h4>
              <div className="flex items-center gap-1.5">
                {hasEvidence && (
                  <span
                    data-testid={`milestone-evidence-pill-${m.id}`}
                    title={preview || 'AI artefact saved as evidence'}
                    className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-[#5E1F8B] dark:bg-[#3a2f0a]/60 dark:text-[#D4AF37]"
                  >
                    <ShieldCheck size={9} /> AI Artefact
                  </span>
                )}
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-white/55">
                  {m.points} pts
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-white/60 mt-1 leading-relaxed">{m.evidence}</p>
            {/* Inline preview of the saved artefact's first field */}
            {hasEvidence && preview && (
              <div
                data-testid={`milestone-preview-${m.id}`}
                className="mt-2 text-[11px] text-slate-600 dark:text-white/65 italic line-clamp-2 border-l-2 border-[#8B3FC5]/40 pl-2"
              >
                &ldquo;{preview}&rdquo;
              </div>
            )}
            {isDone && ts && (
              <p className="text-[10px] text-[#5E1F8B] dark:text-[#8B3FC5] mt-1.5 font-bold">
                Cleared {new Date(ts).toLocaleDateString()}
              </p>
            )}
          </button>
          {/* Right-side action column */}
          <div className="shrink-0 flex flex-col items-stretch gap-1.5">
            <button
              type="button"
              onClick={() => setToolMilestone(m)}
              data-testid={`milestone-ai-tool-${m.id}`}
              title={toolCatalog?.[m.id]?.tool_label || 'Draft this artefact with Yusuf'}
              className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md border border-[#8B3FC5]/40 bg-[#FFF8E5]/60 hover:bg-[#FFF8E5] dark:bg-[#3a2f0a]/40 dark:hover:bg-[#3a2f0a]/60 text-[10px] uppercase tracking-[0.18em] font-bold text-[#5E1F8B] dark:text-[#D4AF37] transition"
            >
              <Wand2 size={11} /> AI Tool
            </button>
            {hasEvidence && (
              <button
                type="button"
                onClick={() => setEvidenceMilestone(m)}
                data-testid={`milestone-view-evidence-${m.id}`}
                title="View the AI artefact saved as evidence"
                className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-white/15 bg-white dark:bg-white/5 hover:border-[#8B3FC5] text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 dark:text-white/70 transition"
              >
                <Eye size={11} /> View
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ve-section max-w-6xl mx-auto" data-testid="startup-roadmap">
      <Link to={`/dashboard/startups/${startupId}`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#5E1F8B] font-bold uppercase tracking-[0.18em]">
        <ArrowLeft size={12} /> Back to {roadmap.startup_name || 'startup'}
      </Link>

      {/* Header — score + summary */}
      <div className="mt-4 ve-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-2">
              <Sparkles size={13}/> Scaling Lean roadmap · Ash Maurya
            </div>
            <h1 className="display text-3xl font-bold mt-2">{roadmap.startup_name || 'Startup'}</h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-white/65 max-w-2xl">
              An opinionated path from idea to scale. Tick a milestone only when you have the evidence to defend it in front of the AI Council.
            </p>
          </div>
          <div className="flex items-center gap-5">
            <ProgressRing percent={roadmap.score} size={84} />
            <div>
              <div className="ve-eyebrow text-slate-400 dark:text-white/45 flex items-center gap-2">
                OOH score
                <OOHPatentBadge variant="pill" onClick={() => setOohOpen(true)} />
              </div>
              <div className="display text-2xl font-bold mt-0.5">{roadmap.score} / 100</div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] dark:text-[#8B3FC5] mt-1">
                Now: {roadmap.current_stage_label}
              </div>
            </div>
          </div>
        </div>

        {/* Stage rings */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          {stagesArr.map(([id, st], i) => {
            const Icon = STAGE_ICONS[id] || Target;
            const pct = roadmap.stages?.[id]?.percent ?? 0;
            const isCurrent = roadmap.current_stage === id;
            return (
              <div
                key={id}
                className={`rounded-lg p-3 border-2 transition ${isCurrent ? 'border-[#8B3FC5] bg-[#FFF8E5] dark:bg-[#3a2f0a]/50' : 'border-slate-200 dark:border-white/10'}`}
                style={{ animation: `ve-pillar-in 0.45s ease ${i * 90}ms both` }}
                data-testid={`roadmap-stage-${id}`}
              >
                <div className="flex items-center gap-3">
                  <ProgressRing percent={pct} size={52} color={isCurrent ? '#8B3FC5' : '#94a3b8'} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 dark:text-white/45">
                      <Icon size={10}/> {st.eyebrow}
                    </div>
                    <div className="text-sm font-bold leading-tight mt-0.5">{st.label}</div>
                    <div className="text-[10px] text-slate-500 dark:text-white/55 mt-0.5">
                      {roadmap.stages?.[id]?.earned}/{roadmap.stages?.[id]?.max} pts
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Yusuf's OOH Guidance */}
      <div className="mt-6 ve-card p-6 lg:p-7 relative overflow-hidden" data-testid="ooh-guidance-card">
        <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#8B3FC5]/10 blur-3xl pointer-events-none"/>
        <div className="flex items-start justify-between gap-3 flex-wrap relative">
          <div>
            <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-2">
              <Compass size={13}/> OOH guidance · personalised by Yusuf
            </div>
            <h3 className="display text-xl font-bold mt-2 leading-snug">
              {guidance ? 'Your next moves.' : 'Get a tailored plan for this week.'}
            </h3>
            <p className="text-xs text-slate-500 dark:text-white/55 mt-1 max-w-md">
              {guidance
                ? `Based on score ${guidance.based_on_score}/100 · generated ${new Date(guidance.generated_at).toLocaleString()}`
                : "Yusuf reads your milestone list + current stage and tells you exactly what to do next."}
            </p>
          </div>
          <button
            onClick={generateGuidance}
            disabled={gLoading}
            data-testid="ooh-guidance-generate"
            className="ve-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {gLoading
              ? <><Loader2 size={13} className="animate-spin"/> Thinking…</>
              : <><Sparkles size={13}/> {guidance ? 'Re-generate' : 'Generate plan'}</>}
          </button>
        </div>

        {guidance && (
          <div className="mt-5 grid lg:grid-cols-2 gap-4 relative">
            {/* Left: next 3 milestones */}
            <div>
              <div className="ve-eyebrow text-slate-500 dark:text-white/55 mb-2">Focus next</div>
              <div className="space-y-2">
                {(guidance.next_three_milestones || []).map((m, i) => (
                  <div key={`${i}-${m.label || 'milestone'}`} className="rounded-md border border-[#8B3FC5]/30 bg-[#FFF8E5]/40 dark:bg-[#3a2f0a]/30 p-3" data-testid={`ooh-next-${i}`}>
                    <div className="text-sm font-bold flex items-center gap-2">
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#8B3FC5] text-white text-[10px] font-bold">{i+1}</span>
                      {m.label}
                    </div>
                    {m.why && <p className="text-xs text-slate-600 dark:text-white/65 mt-1.5"><b>Why:</b> {m.why}</p>}
                    {m.how_in_one_week && <p className="text-xs text-slate-700 dark:text-white/80 mt-1"><b>This week:</b> {m.how_in_one_week}</p>}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: risk + plan + council */}
            <div className="space-y-3">
              {guidance.biggest_risk && (
                <div className="rounded-md border border-rose-200 dark:border-rose-400/30 bg-rose-50 dark:bg-rose-900/15 p-3" data-testid="ooh-risk">
                  <div className="ve-eyebrow text-rose-600 dark:text-rose-300 flex items-center gap-1.5"><AlertTriangle size={10}/> Biggest risk</div>
                  <p className="text-sm mt-1">{guidance.biggest_risk}</p>
                </div>
              )}
              {Array.isArray(guidance.weekly_plan) && guidance.weekly_plan.length > 0 && (
                <div className="rounded-md border border-slate-200 dark:border-white/10 p-3" data-testid="ooh-weekly-plan">
                  <div className="ve-eyebrow text-slate-500 dark:text-white/55">This week&rsquo;s checklist</div>
                  <ul className="mt-1.5 space-y-1">
                    {guidance.weekly_plan.map((b, i) => (
                      <li key={`wp-${i}-${(b || '').slice(0, 24)}`} className="text-sm flex gap-2"><Check size={13} className="text-[#5E1F8B] dark:text-[#8B3FC5] mt-0.5 shrink-0"/><span>{b}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {guidance.recommended_council_question && (
                <div className="rounded-md border border-[#8B3FC5]/30 p-3" data-testid="ooh-council-q">
                  <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] flex items-center gap-1.5"><MessageSquare size={10}/> Take to council</div>
                  <p className="text-sm italic mt-1">&ldquo;{guidance.recommended_council_question}&rdquo;</p>
                </div>
              )}
              {guidance.morale && (
                <p className="text-xs italic text-slate-500 dark:text-white/55 pt-1" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                  — {guidance.morale}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Milestones grouped by stage */}
      <div className="mt-6 space-y-6">
        {stagesArr.map(([stageId, stage]) => (
          <section key={stageId}>
            <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">{stage.label}</div>
            <p className="text-sm text-slate-600 dark:text-white/65 mt-1 max-w-2xl">{stage.summary}</p>
            <div className="mt-3 space-y-2">
              {stage.milestones.map((m) => <MilestoneCard key={m.id} m={m} />)}
            </div>
          </section>
        ))}

        {/* OOH Pillars — orthogonal to the stages */}
        {pillarsArr.length > 0 && (
          <div className="pt-4 border-t-2 border-[#8B3FC5]/30">
            <div className="flex items-end justify-between gap-3 flex-wrap mb-2">
              <div>
                <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Omniscient Orbital Harmony · Pillars</div>
                <h2 className="display text-2xl font-bold mt-1">Beyond Scaling Lean.</h2>
                <p className="text-sm text-slate-600 dark:text-white/65 mt-1 max-w-2xl">
                  Four parallel dimensions a sovereign-grade holding company evaluates every venture against. These compound with — they don&rsquo;t replace — the lean track.
                </p>
              </div>
              {roadmap.weakest_pillar_label && (
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-900/15 dark:text-amber-300">
                  Weakest pillar: {roadmap.weakest_pillar_label.split(' · ')[0]}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {pillarsArr.map(([id, st], i) => {
                const pct = roadmap.stages?.[id]?.percent ?? 0;
                const isWeakest = roadmap.weakest_pillar === id;
                return (
                  <div
                    key={id}
                    className={`rounded-lg p-3 border-2 transition ${isWeakest ? 'border-amber-300 bg-amber-50/40 dark:bg-amber-900/10' : 'border-slate-200 dark:border-white/10'}`}
                    style={{ animation: `ve-pillar-in 0.5s ease ${i * 90}ms both` }}
                    data-testid={`pillar-summary-${id}`}
                  >
                    <div className="flex items-center gap-3">
                      <ProgressRing percent={pct} size={52} color={isWeakest ? '#F59E0B' : '#8B3FC5'} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 dark:text-white/45">{st.eyebrow}</div>
                        <div className="text-sm font-bold leading-tight mt-0.5">{st.label}</div>
                        <div className="text-[10px] text-slate-500 dark:text-white/55 mt-0.5">
                          {roadmap.stages?.[id]?.earned}/{roadmap.stages?.[id]?.max} pts
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {pillarsArr.map(([pillarId, pillar]) => (
              <section key={pillarId} className="mt-6">
                <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">{pillar.label}</div>
                <p className="text-sm text-slate-600 dark:text-white/65 mt-1 max-w-2xl">{pillar.summary}</p>
                <div className="mt-3 space-y-2">
                  {pillar.milestones.map((m) => <MilestoneCard key={m.id} m={m} />)}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Investor-Ready Exports — PDF download + magic-link shares */}
        <DDPacketPanel
          startupId={startupId}
          startupName={roadmap.startup_name}
        />
      </div>

      {/* OOH brief + per-milestone AI toolkit + evidence viewer dialogs */}
      <OOHBriefDialog open={oohOpen} onClose={() => setOohOpen(false)} />
      <MilestoneToolDialog
        open={!!toolMilestone}
        milestone={toolMilestone}
        startupId={startupId}
        toolCatalog={toolCatalog}
        isCleared={toolMilestone ? completed.has(toolMilestone.id) : false}
        onCleared={(rmDoc) => {
          // The server returned the updated roadmap doc; merge into local
          // state so the milestone instantly shows as cleared + the
          // AI-artefact pill appears without a refetch.
          setRoadmap((prev) => ({ ...prev, ...rmDoc }));
        }}
        onClose={() => setToolMilestone(null)}
      />
      <EvidenceViewDialog
        open={!!evidenceMilestone}
        milestone={evidenceMilestone}
        startupId={startupId}
        onClose={() => setEvidenceMilestone(null)}
        onRestored={(data) => {
          // Update the milestone's evidence in the parent roadmap state
          // so the inline preview + AI-artefact pill reflects the
          // restored draft immediately.
          setRoadmap((prev) => ({
            ...prev,
            evidence: { ...(prev.evidence || {}), [data.milestone_id]: data.evidence },
          }));
        }}
        onRegenerate={() => {
          const m = evidenceMilestone;
          setEvidenceMilestone(null);
          setToolMilestone(m);
        }}
      />
    </div>
  );
}
